import { useEffect, useRef, useState } from "react";
import api, { fmtMoney, fmtDate, apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Package, Warehouse, ArrowLeftRight, Layers, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function ArticleDetailDialog({ articleId, onOpenChange }) {
  const [data, setData] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [newLot, setNewLot] = useState(null);
  const formRef = useRef();

  const load = () => { if (articleId) api.get(`/articles/${articleId}`).then((r) => setData(r.data)).catch(() => setData({})); };
  useEffect(() => {
    if (articleId) {
      setData(null); setNewLot(null); load();
      api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
      api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    }
  }, [articleId]);

  const gereLot = data?.article?.gere_par_lot;
  const startLot = () => setNewLot({ reference: "", date_entree: new Date().toISOString().slice(0, 10), date_expiration: "", quantite: 0, prix_achat: data?.article?.prix_achat || 0, magasin_id: "", fournisseur_id: data?.article?.fournisseur_id || "", statut: "Actif" });
  const saveLot = async () => {
    if (!newLot.reference) { toast.error("Référence du lot requise"); return; }
    try { await api.post("/lots", { ...newLot, article_id: articleId, quantite: Number(newLot.quantite), prix_achat: Number(newLot.prix_achat) }); toast.success("Lot enregistré"); setNewLot(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delLot = async (id) => { if (!window.confirm("Supprimer ce lot ?")) return; await api.delete(`/lots/${id}`); toast.success("Supprimé"); load(); };

  return (
    <Dialog open={!!articleId} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="article-detail">
        {!data ? (
          <><DialogHeader className="sr-only"><DialogTitle>Fiche article</DialogTitle></DialogHeader><Loader /></>
        ) : !data.article ? (
          <><DialogHeader className="sr-only"><DialogTitle>Fiche article</DialogTitle></DialogHeader><p className="py-8 text-center text-slate-400">Article introuvable</p></>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-head flex items-center gap-3">
                <Package className="h-5 w-5 text-brand-600" />{data.article.designation}
                <span className="font-mono text-xs font-normal text-slate-400">{data.article.reference}</span>
              </DialogTitle>
            </DialogHeader>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <Stat label="Stock total" value={`${data.article.stock ?? 0} ${data.article.unite}`} />
              <Stat label="Prix d'achat" value={fmtMoney(data.article.prix_achat)} />
              <Stat label="Prix de vente" value={fmtMoney(data.article.prix_vente)} />
              <Stat label="Stock min." value={`${data.article.stock_min ?? 0}`} />
            </div>

            {gereLot && (
              <>
                <div className="flex items-center justify-between mt-3">
                  <p className="flex items-center gap-2 text-sm font-head font-semibold text-slate-700"><Layers className="h-4 w-4 text-brand-600" />Lots</p>
                  <Button size="sm" variant="outline" onClick={startLot} data-testid="add-lot"><Plus className="h-4 w-4 mr-1" />Nouveau lot</Button>
                </div>
                {newLot && (
                  <div ref={formRef} className="rounded-lg border bg-slate-50 p-3 grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="lot-form">
                    <div><Label className="text-[10px]">Référence</Label><Input value={newLot.reference} onChange={(e) => setNewLot({ ...newLot, reference: e.target.value })} data-testid="lot-reference" className="h-8" /></div>
                    <div><Label className="text-[10px]">Date d'entrée</Label><Input type="date" value={newLot.date_entree} onChange={(e) => setNewLot({ ...newLot, date_entree: e.target.value })} className="h-8" /></div>
                    <div><Label className="text-[10px]">Expiration</Label><Input type="date" value={newLot.date_expiration} onChange={(e) => setNewLot({ ...newLot, date_expiration: e.target.value })} className="h-8" /></div>
                    <div><Label className="text-[10px]">Quantité</Label><Input type="number" value={newLot.quantite} onChange={(e) => setNewLot({ ...newLot, quantite: e.target.value })} data-testid="lot-quantite" className="h-8" /></div>
                    <div><Label className="text-[10px]">Prix d'achat</Label><Input type="number" value={newLot.prix_achat} onChange={(e) => setNewLot({ ...newLot, prix_achat: e.target.value })} className="h-8" /></div>
                    <div><Label className="text-[10px]">Magasin</Label>
                      <select value={newLot.magasin_id} onChange={(e) => setNewLot({ ...newLot, magasin_id: e.target.value })} className="h-8 w-full rounded-md border text-sm px-2">
                        <option value="">—</option>{warehouses.map((w) => <option key={w.id} value={w.id}>{w.nom}</option>)}
                      </select>
                    </div>
                    <div className="col-span-2 md:col-span-3 flex justify-end gap-2">
                      <Button size="sm" variant="ghost" onClick={() => setNewLot(null)}>Annuler</Button>
                      <Button size="sm" onClick={saveLot} data-testid="lot-save" className="bg-brand-600 hover:bg-brand-700 text-white">Enregistrer le lot</Button>
                    </div>
                  </div>
                )}
                {(data.lots || []).length === 0 ? <p className="text-xs text-slate-400 pl-6">Aucun lot enregistré</p> : (
                  <div className="space-y-1.5">
                    {data.lots.map((l) => (
                      <div key={l.id} className="flex items-center gap-3 rounded-lg border bg-white px-3 py-2 text-sm" data-testid={`lot-row-${l.id}`}>
                        <span className="font-mono text-xs font-semibold text-slate-700 w-24">{l.reference}</span>
                        <span className="text-xs text-slate-500">{l.date_expiration ? `Exp. ${fmtDate(l.date_expiration)}` : "—"}</span>
                        <span className="text-xs text-slate-500 flex-1 truncate">{l.magasin_name || ""}</span>
                        <span className="font-mono font-semibold">{l.quantite}</span>
                        <Button size="icon" variant="ghost" onClick={() => delLot(l.id)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            <p className="flex items-center gap-2 text-sm font-head font-semibold text-slate-700 mt-3"><Warehouse className="h-4 w-4 text-brand-600" />Stock par magasin</p>
            {data.levels.length === 0 ? <p className="text-xs text-slate-400 pl-6">Aucun stock enregistré</p> : (
              <div className="space-y-1.5">
                {data.levels.map((l, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg border bg-slate-50/60 px-3 py-2">
                    <span className="text-sm text-slate-700">{l.magasin_name}</span>
                    <span className="font-mono text-sm font-semibold text-slate-800">{l.quantite} {data.article.unite}</span>
                  </div>
                ))}
              </div>
            )}

            <p className="flex items-center gap-2 text-sm font-head font-semibold text-slate-700 mt-3"><ArrowLeftRight className="h-4 w-4 text-brand-600" />Derniers mouvements</p>
            {data.movements.length === 0 ? <p className="text-xs text-slate-400 pl-6">Aucun mouvement</p> : (
              <div className="space-y-1.5">
                {data.movements.map((m) => (
                  <div key={m.id} className="flex items-center justify-between rounded-lg border bg-white px-3 py-2 text-sm">
                    <span className="text-xs text-slate-500 w-24">{fmtDate(m.date)}</span>
                    <span className="font-medium text-slate-700 w-24">{m.type}</span>
                    <span className="flex-1 text-slate-500 truncate px-2">{m.magasin_name}{m.magasin_dest_name ? ` → ${m.magasin_dest_name}` : ""}</span>
                    <span className="font-mono font-semibold">{m.quantite}</span>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border bg-white p-3">
      <p className="text-[10px] uppercase font-mono text-slate-400">{label}</p>
      <p className="font-mono font-bold text-slate-800 text-sm">{value}</p>
    </div>
  );
}
